/**
 * StatusPill — web mirror of `mobile/components/StatusPill.tsx`.
 *
 * Small rounded, surface-tinted pill communicating row / listing state at a
 * glance. Variant names + colour mapping mirror the mobile component exactly
 * (semantic names, not colours) so a screen reads identically across web and
 * native:
 *
 *   - overdue  → alert (something went wrong)
 *   - due      → mango (locked / awaiting action)
 *   - upcoming → neutral grey (not acted on yet)
 *   - active   → leaf container (in progress / completed)
 *   - sold     → solid leaf (closed positively)
 *
 * Per `preview/status-pills.html`: full corner radius, 11px bold uppercase
 * label, optional leading icon. The web variant accepts a Material Symbols
 * ligature `icon` name (rendered via the kit `Icon`).
 */
import { Box } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';

import { useColorMode } from '../theme/ColorMode.js';
import { mdSysColor, radius, spacing, typography } from '../theme/tokens.js';
import { Icon } from './Icon.js';

export type StatusPillVariant = 'overdue' | 'due' | 'upcoming' | 'active' | 'sold';

export interface StatusPillProps {
  variant: StatusPillVariant;
  children: ReactNode;
  /** Optional Material Symbols ligature name shown left of the label. */
  icon?: string;
  /** Pass-through className for layout composition. */
  className?: string;
}

export const StatusPill = forwardRef<HTMLSpanElement, StatusPillProps>(function StatusPill(
  { variant, children, icon, className },
  ref,
) {
  // Mode-aware: map each variant onto MD3 system roles so the pill tints
  // correctly in BOTH light and dark. Mapping is verbatim from
  // preview/status-pills.html (the .overdue/.due/.upcoming/.active/.sold rules):
  // overdue uses the SOLID error role (not errorContainer), due the SOLID
  // tertiary role, upcoming surfaceContainerHigh, active secondaryContainer,
  // sold the solid secondary. Web and mobile StatusPill share this mapping.
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  const VARIANT_COLORS: Record<StatusPillVariant, { bg: string; fg: string }> = {
    overdue: { bg: sys.error, fg: sys.onError },
    due: { bg: sys.tertiary, fg: sys.onTertiary },
    upcoming: { bg: sys.surfaceContainerHigh, fg: sys.onSurfaceVariant },
    active: { bg: sys.secondaryContainer, fg: sys.onSecondaryContainer },
    sold: { bg: sys.secondary, fg: sys.onSecondary },
  };
  const { bg, fg } = VARIANT_COLORS[variant];
  return (
    <Box
      component="span"
      ref={ref}
      className={className}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        backgroundColor: bg,
        color: fg,
        px: `${spacing.s3}px`,
        py: '3px',
        borderRadius: `${radius.full}px`,
        fontFamily: typography.fontFamily,
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1.4,
        letterSpacing: '0.02em', // preview/status-pills.html .pill
        textTransform: 'uppercase',
      }}
    >
      {icon ? <Icon name={icon} size={12} color="inherit" /> : null}
      {children}
    </Box>
  );
});
